import { ChevronDown, ChevronLeft, PhoneCall, X } from "lucide-react";
import React, { useContext, useState, useRef, useEffect } from "react";
import InputBox from "../InputBox";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import MessageBubble from "../MessageBubble";
import CustomImage from "../CustomImage";
import { Message } from "../../types/message";
import { ChatContext } from "../ChatContextProvider";

dayjs.extend(relativeTime);

const ChatWindow = () => {
  const {
    appState,
    setAppState,
    messages,
    sendMessage,
    conversationLoading,
    typingIndicator,
    sendTypingIndicator,
    handleOptionSelect,
    setCurrentConversation,
    conversationDetails,
    handleWindowClose,
  } = useContext(ChatContext);

  const [isMobile, setMobile] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);

  const handleSendMessage = (userMessage: Message) => {
    sendMessage(userMessage);
  };

  useEffect(() => {
    if (messagesEndRef.current && isScrolledToBottom) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    }
  }, [messages, typingIndicator]);

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;
    const threshold = 50; // Adjust as needed
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      setIsScrolledToBottom(true);
    } else {
      setIsScrolledToBottom(false);
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      setIsScrolledToBottom(true);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', (event) => {
        event.stopPropagation();
    }, { passive: false });

    return () => {
        window.removeEventListener('scroll', (event) => {
            event.stopPropagation();
        });
    };
}, []);

  const formatDateSeparator = (date: Date) => {
    return dayjs(date).format("MMMM D");
  };

  const shouldShowDateSeparator = (currentIndex: number) => {
    if (currentIndex === 0) return true;
    const currentMessageDate = dayjs(messages[currentIndex].createdAt).startOf(
      "day"
    );
    const previousMessageDate = dayjs(
      messages[currentIndex - 1].createdAt
    ).startOf("day");
    return !currentMessageDate.isSame(previousMessageDate);
  };

  const isFirstInGroup = (index: number) => {
    const currentMessage = messages[index];

    const hasContent =
      (currentMessage.attachments && currentMessage.attachments.length > 0) ||
      (currentMessage.options && currentMessage.options.length > 0) ||
      // @ts-ignore
      currentMessage.content.trim().length > 0;

    if (!hasContent) {
      return false;
    }

    if (index === 0) return true;

    const currentMessageDate = dayjs(currentMessage.createdAt).startOf("day");
    const previousMessage = messages[index - 1];
    const previousMessageDate = dayjs(previousMessage.createdAt).startOf("day");

    if (!currentMessageDate.isSame(previousMessageDate)) return true;

    if (currentMessage.sender_type !== previousMessage.sender_type) return true;

    return false;
  };

  const isLastInGroup = (index: number) => {
    if (index === messages.length - 1) return true;
    const currentMessageDate = dayjs(messages[index].createdAt).startOf("day");
    const nextMessageDate = dayjs(messages[index + 1].createdAt).startOf("day");
    if (!currentMessageDate.isSame(nextMessageDate)) return true;
    return messages[index].sender_type !== messages[index + 1].sender_type;
  };

  const isLastMessage = (index: number) => {
    return index === messages.length - 1;
  };

  useEffect(() => {
    const applyResponsiveClasses = () => {
      if (window.parent) {
        const parentWidth = window.parent.innerWidth;

        if (parentWidth < 768) {
          setMobile(true);
        } else {
          setMobile(false);
        }
      }
    };

    applyResponsiveClasses();

    window.parent.addEventListener("resize", applyResponsiveClasses);

    return () => {
      window.parent.removeEventListener("resize", applyResponsiveClasses);
    };
  }, []);

  const hasOptions =
    messages.length > 0 &&
    (messages[messages.length - 1].sender_type === "bot" ||
      messages[messages.length - 1].sender_type === "agent") &&
    messages[messages.length - 1].options &&
    // @ts-ignore
    messages[messages.length - 1].options.length > 0;

  // Handle user typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    sendTypingIndicator(value.length > 0);
  };

  const moveBack = () => {
    setAppState({
      ...appState,
      currentScreen: appState.previousScreen,
      previousScreen: appState.previousScreen,
    });
    setCurrentConversation(null);
  };

  if (conversationLoading) {
    return (
      <div
        className={`relative flex flex-col h-full w-full bg-background overflow-hidden border ${
          isMobile ? "rounded-none" : "rounded-xl"
        }`}
      >
        <div className="p-4 border-b text-gray-700 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button
              onClick={() => moveBack()}
              className="hover:bg-gray-300 rounded-lg p-2 outline-none focus:outline-none"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="font-semibold">Chat</span>
          </div>
          <button
            onClick={() => handleWindowClose()}
            className="text-2xl leading-none focus:outline-none"
            aria-label="Close Chat"
          >
            <X className="size-5" />
          </button>
        </div>
        <div className="flex-1 p-3 overflow-y-auto overflow-x-hidden">
          <div className="h-40 flex justify-center items-center">
            <svg
              aria-hidden="true"
              className="size-10 animate-spin text-secondary-500"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="#e5e5e5"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative flex flex-col h-full w-full bg-background overflow-hidden border ${
        isMobile ? "rounded-none" : "rounded-xl"
      }`}
    >
      <div className="p-4 border-b text-gray-700 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <button
            onClick={() => moveBack()}
            className="hover:bg-gray-300 rounded-lg p-2 outline-none focus:outline-none"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="font-semibold">Chat</span>
        </div>
        <div className="flex justify-center items-center">
          <button
            onClick={() => handleWindowClose()}
            className="text-2xl leading-none focus:outline-none"
            aria-label="Close Chat"
          >
            <X className="size-5" />
          </button>
        </div>
      </div>

      <div
        className="relative flex-1 p-3 overflow-y-auto overflow-x-hidden"
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {messages.map((message: any, index: number) => (
          <React.Fragment key={index}>
            {shouldShowDateSeparator(index) && (
              <div className="flex justify-center my-4">
                <span className="text-xs text-gray-500">
                  {formatDateSeparator(message.createdAt)}
                </span>
              </div>
            )}
            {message.sender_type === "system" ? (
              <div className="flex justify-center my-4">
                <span className="text-xs text-gray-600 italic">
                  {message.content}
                </span>
              </div>
            ) : (
              <MessageBubble
                isMobile={isMobile}
                message={message}
                isFirstInGroup={isFirstInGroup(index)}
                isLastMessage={isLastMessage(index)}
                isLastInGroup={isLastInGroup(index)}
                onOptionSelect={
                  (message.sender_type === "bot" ||
                    message.sender_type === "agent") &&
                  message.options
                    ? handleOptionSelect
                    : undefined
                }
                onSend={handleSendMessage}
              />
            )}
          </React.Fragment>
        ))}

        {typingIndicator && (
          <div className="flex justify-start mb-6">
            <div className="flex-shrink-0 mr-2">
              <CustomImage
                src={typingIndicator.metadata?.avatar_url || ""}
                alt={typingIndicator.metadata?.first_name || ""}
                size={38}
              />
            </div>
            <div className="relative flex items-center">
              <div className="border rounded-full px-4 py-4 flex items-center space-x-2">
                <div className="typing-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      {!isScrolledToBottom && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-32 right-4 p-2 bg-primary text-white rounded-full shadow-md"
          >
            <ChevronDown/>
          </button>
        )}
      <InputBox
        onSend={handleSendMessage}
        disable={hasOptions}
        onChange={handleInputChange}
      />
    </div>
  );
};

export default ChatWindow;
